import React from 'react';

import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { Box, Button } from '@mui/material';

import { Form, FormSpy } from '@hrlogics/react-components/form';

import { useGetEmployersListForVerificationRequestQuery, VerifierCredentialStatus } from 'src/api';
import { FullscreenLoader, Tooltip } from 'src/components/common';
import {
  VerificationRequestCreateFormPermissiblePurposeField,
  VerificationRequestCreateFormVerificationReportTypeBasicField,
} from 'src/components/verificationRequest';
import { InboxItemContentPaymentBlock } from 'src/components/verificationRequest/VerificationRequestCreateByCommercialVerifierModalForm/components';
import { ModalId } from 'src/constants/modals';
import { VerificationRequestCreateFormFields } from 'src/constants/verificationRequest';
import { useConsumerById } from 'src/hooks/consumer';
import { useModal } from 'src/hooks/shared';
import {
  useVerificationRequest,
  useVerificationRequestFormData,
  useVerificationRequestProcessing,
} from 'src/hooks/verificationRequest';
import { useCurrentUserVerifier } from 'src/hooks/verifier';
import {
  ApproveVerificationRequestByVerifierModalArgs,
  DeclineVerificationRequestByVerifierModalArgs,
} from 'src/modals/verificationRequest';
import { SxProp } from 'src/types/mui';
import { validateWithYupSchema } from 'src/utils/yup';

import {
  COMMERCIAL_VERIFICATION_TYPES_OPTIONS,
  GOVERNMENT_VERIFICATION_TYPES_OPTIONS,
} from './constants';
import { InboxItemVerificationRequestReviewInformationFormValues } from './types';
import { verificationRequestReviewInformationFormValidationSchema } from './utils';

type InboxItemContentVerificationRequestReviewInformationFormProps = {
  inboxItemId: string;
  disabled?: boolean;
};

export const InboxItemContentVerificationRequestReviewInformationForm: React.FC<
  InboxItemContentVerificationRequestReviewInformationFormProps
> = ({ inboxItemId, disabled }) => {
  const { openModal } = useModal();

  const { isVerifierResponding } = useVerificationRequestProcessing();
  const isVerifierResponseInProgress = isVerifierResponding(inboxItemId);

  const {
    isFormInfoLoading,
    isEmployersHavePayrollsInfo,
    setIsEmployeeExists,
    setIsEmployeeInfoLoading,
    setIsEmployersInfoLoading,
    setIsEmployersHavePayrollsInfo,
  } = useVerificationRequestFormData();

  const { isCommercialVerifier, verifierId, verifier } = useCurrentUserVerifier();

  const { verificationRequest } = useVerificationRequest({ inboxItemId });

  const { consumer, loading: isEmployeeLoading } = useConsumerById(
    verificationRequest?.employeeId,
    { skip: !isCommercialVerifier },
  );

  const { data, loading: isEmployersLoading } = useGetEmployersListForVerificationRequestQuery({
    variables: { inboxItemId },
    skip: !isCommercialVerifier,
  });

  const isThereInactiveEmployers = React.useMemo(
    () => data?.getEmployersListForVerificationRequest.isThereInactiveEmployers ?? false,
    [data?.getEmployersListForVerificationRequest.isThereInactiveEmployers],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: only on employee change
  React.useEffect(() => {
    if (consumer) setIsEmployeeExists(true);
  }, [consumer]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only on employers change
  React.useEffect(() => {
    const isEmployersHavePayrollsInfo =
      data?.getEmployersListForVerificationRequest.employers.some(
        employer => employer.isPayrollsAvailable,
      ) ?? false;
    setIsEmployersHavePayrollsInfo(isEmployersHavePayrollsInfo);
  }, [data?.getEmployersListForVerificationRequest.employers]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only on isEmployeeLoading change
  React.useEffect(() => {
    setIsEmployeeInfoLoading(isEmployeeLoading);
  }, [isEmployeeLoading]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only on isEmployersLoading change
  React.useEffect(() => {
    setIsEmployersInfoLoading(isEmployersLoading);
  }, [isEmployersLoading]);

  const tooltipTitle = React.useMemo(() => {
    if (isThereInactiveEmployers) {
      return 'One or some of the employers included in this verification request are no longer active on the platform';
    }
    return '';
  }, [isThereInactiveEmployers]);

  const initialValues = React.useMemo(
    (): Partial<InboxItemVerificationRequestReviewInformationFormValues> => ({
      [VerificationRequestCreateFormFields.verificationReportType]: isCommercialVerifier
        ? COMMERCIAL_VERIFICATION_TYPES_OPTIONS[0].value
        : GOVERNMENT_VERIFICATION_TYPES_OPTIONS[0].value,
      [VerificationRequestCreateFormFields.error]: null,
    }),
    [isCommercialVerifier],
  );

  const handleApprove = React.useCallback(
    (values: InboxItemVerificationRequestReviewInformationFormValues) => {
      openModal(ModalId.ApproveVerificationRequestByVerifier, {
        inboxItemId,
        paymentMethodId: values.paymentMethod,
        permissiblePurposeId: values.permissiblePurposeId,
        verificationRequestType: values.verificationReportType,
      } satisfies ApproveVerificationRequestByVerifierModalArgs);
    },

    [openModal, inboxItemId],
  );

  const handleDecline = React.useCallback(() => {
    openModal(ModalId.DeclineVerificationRequestByVerifier, {
      inboxItemId,
    } satisfies DeclineVerificationRequestByVerifierModalArgs);
  }, [openModal, inboxItemId]);

  if (isEmployeeLoading || isEmployersLoading) {
    return <FullscreenLoader />;
  }

  return (
    <Form<InboxItemVerificationRequestReviewInformationFormValues>
      initialValues={initialValues}
      onSubmit={handleApprove}
      subscription={{ valid: true, submitting: true }}
      containerProps={{ mt: 4 }}
      validate={validateWithYupSchema(verificationRequestReviewInformationFormValidationSchema)}
    >
      {({ submitting, valid }) => {
        return (
          <React.Fragment>
            <Box display="flex" flexDirection="column" gap={2.5}>
              {isCommercialVerifier ? (
                <VerificationRequestCreateFormVerificationReportTypeBasicField
                  options={COMMERCIAL_VERIFICATION_TYPES_OPTIONS}
                  disabled={isFormInfoLoading || !isEmployersHavePayrollsInfo}
                  showIncomeDataWarning={!isFormInfoLoading && !isEmployersHavePayrollsInfo}
                />
              ) : (
                <VerificationRequestCreateFormVerificationReportTypeBasicField
                  options={GOVERNMENT_VERIFICATION_TYPES_OPTIONS}
                />
              )}

              <VerificationRequestCreateFormPermissiblePurposeField verifierId={verifierId} />
            </Box>

            <FormSpy<InboxItemVerificationRequestReviewInformationFormValues>
              subscription={{ values: true, errors: true }}
            >
              {({ values }) => (
                <React.Fragment>
                  {isCommercialVerifier && (
                    <InboxItemContentPaymentBlock
                      inboxItemId={inboxItemId}
                      verificationReportType={values.verificationReportType}
                    />
                  )}
                </React.Fragment>
              )}
            </FormSpy>

            <Box display="flex" gap={4} mt={4} justifyContent="center">
              <Button
                data-e2e-element="decline-request"
                variant="outlined"
                color="error"
                onClick={() => handleDecline()}
                disabled={
                  disabled ||
                  submitting ||
                  isVerifierResponseInProgress ||
                  verifier?.credentialStatus !== VerifierCredentialStatus.Credentialed
                }
                sx={buttonStyles}
              >
                Decline
              </Button>

              <Tooltip title={tooltipTitle} placement="top" arrow>
                <Box>
                  <Button
                    data-e2e-element="approve-request"
                    type="submit"
                    variant="outlined"
                    disabled={
                      disabled ||
                      !valid ||
                      submitting ||
                      isFormInfoLoading ||
                      isVerifierResponseInProgress ||
                      verifier?.credentialStatus !== VerifierCredentialStatus.Credentialed ||
                      isThereInactiveEmployers
                    }
                    loading={submitting || isFormInfoLoading || isVerifierResponseInProgress}
                    sx={buttonStyles}
                    endIcon={
                      isThereInactiveEmployers ? <InfoOutlinedIcon sx={{ height: '20px' }} /> : null
                    }
                  >
                    Approve
                  </Button>
                </Box>
              </Tooltip>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};

const buttonStyles: SxProp = {
  paddingX: 6,
};
